import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { showGlobalModal } from 'core/components/GlobalModal';
import { dmTextGenerationApi } from 'features/DigitalMarketing/api/dmTextGenerationApi';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import { DMFormField } from 'features/DigitalMarketing/components/DMFormField';
import { DMSelectField } from 'features/DigitalMarketing/components/DMSelectField';
import { DMFileUploadDropzone } from 'features/DigitalMarketing/components/DMFileUploadDropzone';
import { DMImagePreviewWithActions } from 'features/DigitalMarketing/components/DMImagePreviewWithActions';
import { DMAiDescriptionBox } from 'features/DigitalMarketing/components/DMAiDescriptionBox';
import { DMQualityHint } from 'features/DigitalMarketing/components/DMQualityHint';
import { DMFormActions } from 'features/DigitalMarketing/components/DMFormActions';
import { DMDiv } from 'features/DigitalMarketing/components/Primitives';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import {
  recognitionsAchievementsApi,
  RecognitionAchievementItem,
  RecognitionAchievementPayload,
} from 'features/DigitalMarketing/pages/EditWebsiteContent/api/recognitionsAchievementsApi';
import { useAiDescription } from 'features/DigitalMarketing/pages/EditWebsiteContent/hooks/useAiDescription';
import { useWebsiteImageUpload } from 'features/DigitalMarketing/pages/EditWebsiteContent/hooks/useWebsiteImageUpload';
import { useImageEnhancer } from 'features/DigitalMarketing/hooks/useImageEnhancer';

type RecognitionAchievementFormMode = 'recognition' | 'achievement';

type FormValues = {
  title: string;
  year: string;
  issuedBy: string;
  issuedFor: string;
  issuedLevel: string;
  description: string;
  image: string;
};

const emptyValues: FormValues = {
  title: '',
  year: '',
  issuedBy: '',
  issuedFor: '',
  issuedLevel: '',
  description: '',
  image: '',
};

const UploadBox = ({
  label,
  image,
  isUploading,
  isEnhancing,
  onUpload,
  onEnhance,
  onPreview,
}: {
  label: string;
  image: string;
  isUploading: boolean;
  isEnhancing: boolean;
  onUpload: (file: File) => void;
  onEnhance: () => void;
  onPreview: () => void;
}) => (
  <DMDiv>
    {image ? (
      <DMImagePreviewWithActions
        imageUrl={image}
        altText={label}
        isUploading={isUploading}
        isEnhancing={isEnhancing}
        shape="rounded"
        imageSize="h-[12vh] w-[12vh]"
        showRemove={false}
        fileTypeHint=""
        uploadLabel={image ? 'Change Photo' : 'Upload Photo'}
        onPreview={onPreview}
        onUpload={onUpload}
        onEnhance={onEnhance}
        layout="column"
      />
    ) : (
      <DMFileUploadDropzone
        className="h-[28vh]"
        isUploading={isUploading}
        onFileSelect={onUpload}
      />
    )}
  </DMDiv>
);

export function RecognitionAchievementForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const editItem = location.state?.item as
    | RecognitionAchievementItem
    | undefined;
  const routeSegment =
    location.pathname.split('/').filter(Boolean).at(-1) || '';
  const isEditMode = routeSegment.startsWith('edit-');
  const mode: RecognitionAchievementFormMode = routeSegment.endsWith(
    'achievement'
  )
    ? 'achievement'
    : 'recognition';
  const isAchievement = mode === 'achievement';
  const title = `${isEditMode ? 'Edit' : 'Add'} ${
    isAchievement ? 'Achievement' : 'Recognition'
  }`;
  const submitLabel = isEditMode ? 'Save Changes' : title;
  const [values, setValues] = React.useState<FormValues>(() =>
    editItem
      ? {
          title: editItem.title,
          year: editItem.year,
          issuedBy: editItem.issuedBy,
          issuedFor: editItem.issuedFor,
          issuedLevel: editItem.issuedLevel,
          description: editItem.description,
          image: editItem.image,
        }
      : emptyValues
  );
  const [previewImageUrl, setPreviewImageUrl] = React.useState('');
  const hasAllDescriptionFields = [
    values.title,
    values.year,
    values.issuedBy,
    values.issuedFor,
    values.issuedLevel,
  ].every(value => value.trim());
  const { isUploadingImage, uploadImage } = useWebsiteImageUpload();
  const { enhanceImage, isEnhancingImage } = useImageEnhancer();

  const handleEnhanceImage = async () => {
    const enhancedUrl = await enhanceImage(values.image);
    if (enhancedUrl) setValues(current => ({ ...current, image: enhancedUrl }));
  };
  const {
    isGeneratingDescription,
    descriptionGenerationsRemaining,
    improveDescription,
  } = useAiDescription({
    generateDescription: async () =>
      dmTextGenerationApi.generateDescription({
        type: 'RECOGNITION',
        title: values.title,
        issued_by: values.issuedBy,
        issued_year: values.year,
        issued_for: values.issuedFor,
        issued_level: values.issuedLevel,
      }),
    emptyMessage:
      'Complete all five fields before generating a description.',
    requiresDescription: false,
  });

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target;
    setValues(current => ({ ...current, [name]: value }));
  };

  const handleUploadImage = async (file: File) => {
    const localPreviewRef = { current: '' };
    const imageUrl = await uploadImage(file, localPreviewUrl => {
      localPreviewRef.current = localPreviewUrl;
      setValues(current => ({ ...current, image: localPreviewUrl }));
    });

    if (imageUrl) {
      setValues(current => ({ ...current, image: imageUrl }));
    } else if (localPreviewRef.current) {
      setValues(current =>
        current.image === localPreviewRef.current
          ? { ...current, image: '' }
          : current
      );
    }
  };

  const handleGenerateDescription = async () => {
    if (!hasAllDescriptionFields) {
      showGlobalModal(
        'Complete all five fields before generating a description.',
        'warning'
      );
      return;
    }

    await improveDescription(values.description, description => {
      setValues(current => ({ ...current, description }));
    });
  };

  const getRecognitionInfoPayload = (): RecognitionAchievementPayload => ({
    IMAGE_URL: values.image,
    TITLE: values.title,
    ISSUED_BY: values.issuedBy,
    ISSUED_YEAR: values.year,
    ISSUED_FOR: values.issuedFor,
    ISSUED_LEVEL: values.issuedLevel,
    DESCRIPTION: values.description,
    CATEGORY: isAchievement ? 'ACHIEVEMENT' : 'RECOGNITION',
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !values.title ||
      !values.year ||
      !values.issuedBy ||
      !values.issuedFor ||
      !values.issuedLevel ||
      !values.description
    ) {
      showGlobalModal('Complete all fields before submitting', 'warning');
      return;
    }

    try {
      const payload = getRecognitionInfoPayload();
      await (isEditMode
        ? recognitionsAchievementsApi.update(
            editItem?.recognitionsAchievementsId || editItem?.id,
            payload
          )
        : recognitionsAchievementsApi.upsert(payload));

      showGlobalModal(
        `${isAchievement ? 'Achievement' : 'Recognition'} ${
          isEditMode ? 'updated' : 'uploaded'
        } successfully`
      );
      navigate(buildEditWebsiteContentPath('recognitions-achievements'));
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to save details'),
        'error'
      );
    }
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[2.2vh] font-inter text-gray-950"
    >
      <DigitalMarketingBreadcrumb
        className="mb-[4vh] shrink-0"
        items={[
          {
            label: 'Edit Website Content',
            path: EDIT_WEBSITE_CONTENT_BASE_PATH,
          },
          {
            label: 'Recognitions & Achievements',
            path: buildEditWebsiteContentPath('recognitions-achievements'),
          },
          { label: title },
        ]}
      />

      <DMDiv
        as="form"
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {isAchievement ? (
          <>
            <DMDiv className="grid shrink-0 grid-cols-2 gap-x-[1.8vw]">
              <DMDiv className="grid grid-cols-2 gap-x-[1vw] gap-y-[1.2vh]">
                <DMFormField
                  required
                  label="Title"
                  name="title"
                  placeholder="Enter Achievement Name"
                  value={values.title}
                  onChange={handleInputChange}
                  className="col-span-2"
                />
                <DMFormField
                  required
                  label="Issued Year"
                  name="year"
                  placeholder="Enter Issued Year"
                  value={values.year}
                  onChange={handleInputChange}
                />
                <DMFormField
                  required
                  label="Issued By"
                  name="issuedBy"
                  placeholder="Enter Name of the Issuer"
                  value={values.issuedBy}
                  onChange={handleInputChange}
                />
                <DMFormField
                  required
                  label="Issued For"
                  name="issuedFor"
                  placeholder="Enter Recipient or Purpose"
                  value={values.issuedFor}
                  onChange={handleInputChange}
                />
                <DMSelectField
                  required
                  label="Issued Level"
                  name="issuedLevel"
                  placeholder="Select Issued Level"
                  value={values.issuedLevel}
                  options={['STATE', 'DISTRICT', 'NATIONAL', 'INTERNATIONAL']}
                  onChange={handleInputChange}
                />
              </DMDiv>
              <DMAiDescriptionBox
                matchContainerHeight
                value={values.description}
                onChange={handleInputChange}
                onGenerate={handleGenerateDescription}
                isGenerating={isGeneratingDescription}
                isGenerateDisabled={!hasAllDescriptionFields}
                generationsRemaining={descriptionGenerationsRemaining}
                required
              />
            </DMDiv>

            <DMDiv className="mt-[1.85vh] shrink-0 mx-auto w-1/2">
              <UploadBox
                label="Achievement Image"
                image={values.image}
                isUploading={isUploadingImage}
                isEnhancing={isEnhancingImage}
                onUpload={handleUploadImage}
                onEnhance={handleEnhanceImage}
                onPreview={() => setPreviewImageUrl(values.image)}
              />
            </DMDiv>
          </>
        ) : (
          <>
            <DMDiv className="grid shrink-0 grid-cols-2 gap-x-[1.8vw]">
              <UploadBox
                label="Recognition Image"
                image={values.image}
                isUploading={isUploadingImage}
                isEnhancing={isEnhancingImage}
                onUpload={handleUploadImage}
                onEnhance={handleEnhanceImage}
                onPreview={() => setPreviewImageUrl(values.image)}
              />
              <DMAiDescriptionBox
                value={values.description}
                onChange={handleInputChange}
                onGenerate={handleGenerateDescription}
                isGenerating={isGeneratingDescription}
                isGenerateDisabled={!hasAllDescriptionFields}
                generationsRemaining={descriptionGenerationsRemaining}
                required
              />
            </DMDiv>

            <DMDiv className="mt-[0.9vh] shrink-0">
              <DMQualityHint />
            </DMDiv>

            <DMDiv className="mt-[1.85vh] grid shrink-0 grid-cols-2 gap-x-[1.8vw] gap-y-[1.2vh]">
              <DMFormField
                required
                label="Award Name"
                name="title"
                placeholder="Enter Award Name"
                value={values.title}
                onChange={handleInputChange}
              />
              <DMFormField
                required
                label="Issued Year"
                name="year"
                placeholder="Enter Issued Year"
                value={values.year}
                onChange={handleInputChange}
              />
              <DMFormField
                required
                label="Issued By"
                name="issuedBy"
                placeholder="Enter Name of the Issuer"
                value={values.issuedBy}
                onChange={handleInputChange}
              />
              <DMFormField
                required
                label="Issued For"
                name="issuedFor"
                placeholder="Enter Recipient or Purpose"
                value={values.issuedFor}
                onChange={handleInputChange}
              />
              <DMSelectField
                required
                label="Issued Level"
                name="issuedLevel"
                placeholder="Select Issued Level"
                value={values.issuedLevel}
                options={['STATE', 'DISTRICT', 'NATIONAL', 'INTERNATIONAL']}
                onChange={handleInputChange}
              />
            </DMDiv>
          </>
        )}

        {isAchievement && (
          <DMDiv className="mt-[0.9vh] shrink-0">
            <DMQualityHint />
          </DMDiv>
        )}

        <DMFormActions
          submitLabel={submitLabel}
          isDisabled={isUploadingImage || isEnhancingImage || isGeneratingDescription}
          showReset={!isEditMode}
          resetLabel="Reset"
          onReset={() => setValues(emptyValues)}
        />
        <ImagePreviewModal
          imageUrl={previewImageUrl}
          alt={values.title || 'Recognition achievement image'}
          onClose={() => setPreviewImageUrl('')}
        />
      </DMDiv>
    </DMDiv>
  );
}
